// Profile scene — identity, stats, sign-in / sign-out, preferences.
// Accounts come from Supabase via useAuth. Display name lives in
// profiles.display_name via useProfile. Stats are computed from useHabits +
// useCheckins so they always match the rest of the app.

import { useMemo, useState, useSyncExternalStore } from 'react';
import { Icon } from '../components/Icon.jsx';
import { apiUrl, isIOS, openExternal } from '../lib/platform.js';
import { openBillingPortal } from '../lib/stripe.js';

// apiUrl from platform.js handles the iOS / Electron / plain-browser split.
// On the iOS Capacitor shell the page is served at capacitor://localhost,
// and on Electron production it's served via file://, so in both cases a
// relative /api/* would resolve against the wrong origin and 404 (or worse,
// fall through to the SPA shell with status 200 — the original "I can log
// back into a deleted account" bug). The helper prepends the absolute
// Vercel deployment URL on those native shells and stays relative on plain
// browser deploys where Vercel serves both the SPA and the functions.
import {
  useHabits, useCheckins, useProfile, useUpdateProfile,
} from '../lib/hooks.js';
import { useAuth } from '../auth/useAuth.js';
import { containsSlur, BLOCKED_MESSAGE } from '../lib/safety.js';
import { sfx } from '../lib/sfx.js';
import { briefStore } from '../lib/briefStore.js';
import { BUNDLES } from '../lib/briefBundles.js';
import * as themeStore from '../lib/themeStore.js';
import {
  getConsent as getAIConsent,
  revokeConsent as revokeAIConsent,
  grantConsent as grantAIConsent,
  subscribeConsent,
} from '../lib/aiConsent.js';
import {
  getPrefs as getRecapPrefs,
  setEnabled as setRecapEnabled,
  setTime as setRecapTime,
  formatTime12 as formatRecapTime,
  subscribePrefs as subscribeRecapPrefs,
} from '../lib/eveningRecap.js';
import {
  getPrefs as getNotifPrefs,
  setTypeEnabled as setNotifTypeEnabled,
  setTypeTime as setNotifTypeTime,
  setQuietHours as setNotifQuietHours,
  subscribePrefs as subscribeNotifPrefs,
  formatTime12 as formatNotifTime,
} from '../lib/notifications.js';
import {
  useEntitlements,
  presentPaywall,
  refreshEntitlements,
} from '../lib/subscription.js';
import {
  restorePurchases,
  isPurchasesAvailable,
} from '../lib/purchases.js';

// Curated voice list for ElevenLabs playback. Most IDs are the canonical
// public voices baked into every ElevenLabs account, so no per-user voice
// setup is required. The Hans Wilmar slot points to a custom voice cloned
// into the Proxera workspace library. Keep the list short and opinionated
// so the picker stays calm.
const VOICE_OPTIONS = [
  { id: 'pNInz6obpgDQGcFmaJgB', name: 'Adam',        tag: 'Deep · American male' },
  { id: '21m00Tcm4TlvDq8ikWAM', name: 'Rachel',      tag: 'Calm · American female' },
  { id: '4yye0QE5YPsKbMOCGGlj', name: 'Hans Wilmar', tag: 'Steady · Conversational male' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Bella',       tag: 'Soft · American female' },
  { id: 'ErXwobaYiN019PkySvjV', name: 'Antoni',      tag: 'Warm · American male' },
  { id: 'TxGEqnHWrfWFTfGW9XjX', name: 'Josh',        tag: 'Young · American male' },
];

// Retention window choices for auto-deleting old briefs. `null` means keep
// briefs forever; the store treats null as disabled.
const RETENTION_OPTIONS = [
  { value: null, label: 'Never',   sub: 'Keep every brief I generate' },
  { value: 30,   label: '30 days', sub: 'Roll out a rolling month of history' },
  { value: 90,   label: '90 days', sub: 'Keep a quarter of briefs on device' },
];

function initialsOf(name) {
  if (!name) return 'YOU';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function formatMonthYear(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const mons = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${mons[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return '—'; }
}

function daysSince(iso) {
  if (!iso) return 0;
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.floor((now - then) / 86400000));
  } catch { return 0; }
}

export function Profile({ onRequestSignIn }) {
  const { status, user, session, signOut, migration, retryMigration } = useAuth();
  const { data: profile } = useProfile();
  const { data: habits = [] } = useHabits();
  const { data: checkins = [] } = useCheckins();
  const updateProfile = useUpdateProfile();

  // Sub-page routing inside the Profile tab.
  const [view, setView] = useState('main'); // 'main' | 'privacy' | 'about' | 'intelligence' | 'appearance' | 'recap'

  // Current theme name — exposed here so the Appearance setting-row can
  // show the active option ('Cosmic' or 'Professional') as its detail text
  // without the user having to drill in.
  const theme = useSyncExternalStore(themeStore.subscribe, themeStore.getSnapshot, themeStore.getSnapshot);

  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameErr, setNameErr] = useState('');

  // Account deletion — Apple guideline 5.1.1(v) requires an in-app
  // delete-account flow for any app that supports account creation.
  // confirmDelete toggles the styled confirmation sheet; deleting is
  // the "request is in flight" flag that disables buttons and shows
  // a spinner-ish affordance; deleteErr surfaces server failures in
  // the confirm sheet so the user knows to retry.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState('');

  const isGuest = status === 'guest';
  const displayName = profile?.displayName || (user?.email ? user.email.split('@')[0] : 'Guest');
  const joinedIso = profile?.createdAt || user?.created_at || null;

  const totalChecks = useMemo(
    () => checkins.filter(c => c.status === 'done').length,
    [checkins]
  );

  // Sub-page guards must sit AFTER every hook above — React requires the
  // same hook order on every render, so we can only bail out once all
  // hooks have been called.
  if (view === 'privacy') {
    return <PrivacyPage onBack={() => { sfx.nav && sfx.nav(); setView('main'); }}/>;
  }
  if (view === 'about') {
    return <AboutPage onBack={() => { sfx.nav && sfx.nav(); setView('main'); }}/>;
  }
  if (view === 'intelligence') {
    return <IntelligenceSettingsPage onBack={() => { sfx.nav && sfx.nav(); setView('main'); }}/>;
  }
  if (view === 'appearance') {
    return <AppearancePage onBack={() => { sfx.nav && sfx.nav(); setView('main'); }}/>;
  }
  if (view === 'recap') {
    return <RecapSettingsPage onBack={() => { sfx.nav && sfx.nav(); setView('main'); }}/>;
  }
  const currentStreak = habits.length > 0
    ? Math.max(...habits.map(h => h.streak || 0))
    : 0;
  const longestStreak = habits.length > 0
    ? Math.max(...habits.map(h => h.longest || 0))
    : 0;

  const beginEditName = () => {
    if (isGuest) return;
    setNameDraft(displayName);
    setNameErr('');
    setEditingName(true);
  };
  const commitName = () => {
    const v = nameDraft.trim();
    if (!v) { setEditingName(false); return; }
    if (containsSlur(v)) { setNameErr(BLOCKED_MESSAGE); return; }
    updateProfile.mutate({ displayName: v });
    setEditingName(false);
    sfx.toast && sfx.toast();
  };

  const handleSignOut = async () => {
    if (!confirm('Sign out? Your data stays synced and will be here when you sign back in.')) return;
    sfx.nav && sfx.nav();
    try { await signOut(); } catch (e) { alert(e?.message || 'Sign-out failed'); }
  };

  const handleSignIn = () => {
    sfx.nav && sfx.nav();
    onRequestSignIn && onRequestSignIn();
  };

  // Fire the account-delete endpoint with the current session's JWT.
  // The serverless handler deletes every user-scoped row and then calls
  // auth.admin.deleteUser, so after a 200 the user row no longer exists
  // anywhere on the server. We clear the local session last so the app
  // flips to the guest state only after the server is known to be clean.
  const handleDeleteAccount = async () => {
    if (deleting) return;
    const token = session?.access_token;
    if (!token) {
      setDeleteErr('No active session. Sign in again and retry.');
      return;
    }
    setDeleting(true);
    setDeleteErr('');
    try {
      const res = await fetch(apiUrl('/api/account-delete'), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      });
      // Defensive: on native, a misrouted call can fall through to the SPA
      // shell which returns 200 with HTML. If we got 200 but the body isn't
      // JSON (or doesn't have ok:true), treat it as a failure so we don't
      // silently sign the user out from a phantom success.
      const ct = res.headers.get('content-type') || '';
      if (res.ok && !ct.includes('application/json')) {
        throw new Error('Delete request did not reach the server. Try again or update the app.');
      }
      if (!res.ok) {
        let msg = `Delete failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }
      // Success. Sign out to clear the local session and flip the UI to
      // the guest state. Swallow any sign-out error — the server user
      // is already gone, the stored tokens are useless, and the auth
      // listener will still fire SIGNED_OUT on a failed sign-out in
      // practice. Do NOT keep the confirm sheet open past this point.
      setConfirmDelete(false);
      try { await signOut(); } catch {}
    } catch (err) {
      setDeleteErr(err?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      {/* Header */}
      <div style={{ padding: '8px 20px 10px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ fontSize: 9 }}>Profile</div>
        <h1 className="title" style={{ marginTop: 6, fontSize: 22 }}>
          The <span className="accent">Operator</span>
        </h1>
      </div>

      {/* Migration banner (inline variant — the global .migration-banner style
          is absolute-positioned for the app shell; here we want inline flow) */}
      {migration?.state === 'running' && (
        <div style={{
          margin: '0 20px 12px', padding: '10px 12px', borderRadius: 14,
          background: 'rgba(45,156,255,0.12)',
          border: '1px solid rgba(45,156,255,0.35)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          display: 'flex', gap: 10, alignItems: 'center',
          fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-2)', lineHeight: 1.4,
        }}>
          Syncing your local data to your account…
        </div>
      )}
      {migration?.state === 'failed' && (
        <div style={{
          margin: '0 20px 12px', padding: '10px 12px', borderRadius: 14,
          background: 'rgba(255,77,106,0.12)',
          border: '1px solid rgba(255,77,106,0.35)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          fontFamily: 'var(--font-body)', fontSize: 11.5, color: '#FFAEBD', lineHeight: 1.4,
        }}>
          <span style={{ flex: 1 }}>Sync failed: {migration.error || 'unknown error'}</span>
          <button onClick={retryMigration} className="auth-link">Retry</button>
        </div>
      )}

      {/* Hero card */}
      <div style={{ padding: '12px 20px 16px' }}>
        <div style={{
          padding: 20,
          borderRadius: 22,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.22), transparent 55%), rgba(19,21,25,0.72)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', position: 'relative' }}>
            <div className="avatar pulse">{initialsOf(displayName)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editingName ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={e => { setNameDraft(e.target.value); setNameErr(''); }}
                  onBlur={commitName}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  maxLength={40}
                  style={{
                    width: '100%', background: 'transparent', border: 'none',
                    borderBottom: '1px solid var(--accent)',
                    color: 'var(--fg-1)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18,
                    padding: '1px 0', outline: 'none',
                  }}
                />
              ) : (
                <div
                  onClick={beginEditName}
                  style={{
                    fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18,
                    color: 'var(--fg-1)', letterSpacing: '-0.01em',
                    cursor: isGuest ? 'default' : 'text',
                  }}
                >
                  {displayName}
                </div>
              )}
              {nameErr && (
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: '#FF8A9E', marginTop: 4 }}>
                  {nameErr}
                </div>
              )}
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)', marginTop: 3 }}>
                {isGuest
                  ? 'Not synced · data lives on this device only'
                  : <>Joined {formatMonthYear(joinedIso)} · <span style={{ color: 'var(--accent-hi)', fontWeight: 600 }}>{daysSince(joinedIso)} days</span></>
                }
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8,
                padding: '4px 9px', borderRadius: 999,
                background: isGuest ? 'rgba(255,181,71,0.12)' : 'rgba(45,156,255,0.12)',
                border: isGuest ? '1px solid rgba(255,181,71,0.3)' : '1px solid rgba(45,156,255,0.3)',
              }}>
                <Icon name={isGuest ? 'info' : 'star'} size={10} color={isGuest ? '#FFB547' : '#FFB547'}/>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 10, color: 'var(--fg-1)', letterSpacing: 0.08 }}>
                  {isGuest ? 'GUEST · NOT SYNCED' : 'SIGNED IN · SYNCED'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 18 }}>
            <div style={{ padding: '10px 8px', borderRadius: 12, background: 'rgba(12,13,15,0.5)', border: '1px solid var(--stroke-1)', textAlign: 'center' }}>
              <div className="grad-text" style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 20, letterSpacing: '-0.02em' }}>{totalChecks}</div>
              <div className="eyebrow" style={{ fontSize: 8, marginTop: 4 }}>Checks</div>
            </div>
            <div style={{ padding: '10px 8px', borderRadius: 12, background: 'rgba(12,13,15,0.5)', border: '1px solid var(--stroke-1)', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 20, color: '#FFB547', letterSpacing: '-0.02em' }}>{currentStreak}</div>
              <div className="eyebrow" style={{ fontSize: 8, marginTop: 4 }}>Streak</div>
            </div>
            <div style={{ padding: '10px 8px', borderRadius: 12, background: 'rgba(12,13,15,0.5)', border: '1px solid var(--stroke-1)', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 20, color: 'var(--fg-1)', letterSpacing: '-0.02em' }}>{longestStreak}</div>
              <div className="eyebrow" style={{ fontSize: 8, marginTop: 4 }}>Longest</div>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div style={{ padding: '0 20px 12px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Preferences</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {/* Evening recap notification — wires through to the sub-page
              where the user toggles enable/disable + the daily time. */}
          <RecapSettingRow onClick={() => { sfx.nav && sfx.nav(); setView('recap'); }}/>
          <div
            className="setting-row"
            style={{ cursor: 'pointer' }}
            onClick={() => { sfx.nav && sfx.nav(); setView('appearance'); }}
          >
            <div className="ico"><Icon name="moon" size={15}/></div>
            <div className="lbl">Appearance</div>
            <div className="det">{
              theme === 'professional-dark' ? 'Executive' :
              theme === 'professional-light' ? 'Relaxed' :
              theme === 'girlboss' ? 'Girlboss' :
              'Cosmic'
            }</div>
            <Icon name="chevron-r" size={14} color="#6E7280"/>
          </div>
          <div
            className="setting-row"
            style={{ cursor: 'pointer' }}
            onClick={() => { sfx.nav && sfx.nav(); setView('intelligence'); }}
          >
            <div className="ico"><Icon name="brain" size={15}/></div>
            <div className="lbl">Intelligence Brief</div>
            <div className="det">Voice · Bundle · Retention</div>
            <Icon name="chevron-r" size={14} color="#6E7280"/>
          </div>
        </div>
      </div>

      {/* Subscription */}
      {!isGuest && (
        <div style={{ padding: '0 20px 12px' }}>
          <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Subscription</div>
          <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
            <SubscriptionRows/>
          </div>
        </div>
      )}

      {/* Data · Account */}
      <div style={{ padding: '0 20px 12px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Data · Account</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {!isGuest && user?.email && (
            <div className="setting-row">
              <div className="ico"><Icon name="mail" size={15}/></div>
              <div className="lbl">Email</div>
              <div className="det" style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
            </div>
          )}
          <div
            className="setting-row"
            style={{ cursor: 'pointer' }}
            onClick={() => { sfx.nav && sfx.nav(); setView('privacy'); }}
          >
            <div className="ico"><Icon name="shield" size={15}/></div>
            <div className="lbl">Privacy</div>
            <Icon name="chevron-r" size={14} color="#6E7280"/>
          </div>
          <div
            className="setting-row"
            style={{ cursor: 'pointer' }}
            onClick={() => { sfx.nav && sfx.nav(); setView('about'); }}
          >
            <div className="ico"><Icon name="info" size={15}/></div>
            <div className="lbl">About Consistency</div>
            <Icon name="chevron-r" size={14} color="#6E7280"/>
          </div>
        </div>
      </div>

      {/* Sign in / out */}
      <div style={{ padding: '0 20px 12px' }}>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {isGuest ? (
            <div className="setting-row" style={{ cursor: 'pointer' }} onClick={handleSignIn}>
              <div className="ico"><Icon name="lock" size={15}/></div>
              <div className="lbl">Sign in · sync across devices</div>
              <Icon name="chevron-r" size={14} color="#6E7280"/>
            </div>
          ) : (
            <>
              <div className="setting-row" style={{ cursor: 'pointer' }} onClick={handleSignOut}>
                <div className="ico danger"><Icon name="logout" size={15}/></div>
                <div className="lbl danger">Sign out</div>
              </div>
              {/* Account deletion is required by App Store guideline 5.1.1(v).
                  The actual delete is irreversible and removes all server
                  data plus the auth user, so it sits behind a confirmation
                  sheet rather than firing immediately on tap. */}
              <div
                className="setting-row"
                style={{ cursor: 'pointer', borderTop: '1px solid var(--stroke-1)' }}
                onClick={() => { sfx.nav && sfx.nav(); setDeleteErr(''); setConfirmDelete(true); }}
              >
                <div className="ico danger"><Icon name="trash" size={15}/></div>
                <div className="lbl danger">Delete account</div>
                <Icon name="chevron-r" size={14} color="#6E7280"/>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '12px 20px 20px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ fontSize: 9, color: 'var(--fg-4)' }}>Proxera Consistency · v1.0</div>
      </div>

      {/* Delete-account confirmation sheet. Mirrors the habit-delete dialog
          on the Dashboard so the destructive-action affordance feels the
          same throughout the app. Backdrop tap dismisses only when not
          mid-request so we don't leave a half-completed delete. */}
      {confirmDelete && (
        <>
          <div
            onClick={() => { if (!deleting) setConfirmDelete(false); }}
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              zIndex: 92, animation: 'fadeIn 140ms ease-out',
            }}
          />
          <div style={{
            position: 'absolute', left: 24, right: 24, top: '32%', zIndex: 93,
            background: 'rgba(20,24,32,0.98)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 20,
            padding: '22px 20px 16px',
            boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
            animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
          }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--fg-1)', marginBottom: 8 }}>
              Delete your account?
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)', lineHeight: 1.5, marginBottom: 14 }}>
              This removes every habit, check-in, goal, agenda, and brief
              tied to your account from our servers and deletes your login.
              It happens immediately and can't be undone.
            </div>
            {deleteErr && (
              <div style={{
                marginBottom: 14, padding: '10px 12px', borderRadius: 12,
                background: 'rgba(255,77,106,0.12)',
                border: '1px solid rgba(255,77,106,0.35)',
                fontFamily: 'var(--font-body)', fontSize: 12, color: '#FFAEBD',
                lineHeight: 1.4,
              }}>
                {deleteErr}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { if (!deleting) setConfirmDelete(false); }}
                disabled={deleting}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid var(--stroke-1)',
                  color: 'var(--fg-2)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                style={{
                  flex: 1, padding: '12px 16px', borderRadius: 999,
                  background: 'linear-gradient(180deg, #FF5A70, #E23A54)',
                  border: '1px solid rgba(255,90,112,0.6)',
                  color: '#ffffff',
                  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 8px 22px rgba(226,58,84,0.45)',
                  opacity: deleting ? 0.8 : 1,
                }}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-page header ────────────────────────────────────────────────
// Shared back-button header for in-Profile sub-pages. Keeps the same
// eyebrow + title layout used elsewhere so the navigation feels native.
function SubPageHeader({ eyebrow, title, accent, onBack }) {
  return (
    <div style={{ padding: '8px 20px 10px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <button
        className="btn-icon"
        onClick={onBack}
        aria-label="Back"
      >
        <Icon name="chevron-l" size={18}/>
      </button>
      <div style={{ flex: 1, textAlign: 'center', paddingRight: 40 }}>
        <div className="eyebrow" style={{ fontSize: 9 }}>{eyebrow}</div>
        <h1 className="title" style={{ marginTop: 6, fontSize: 22 }}>
          {title} <span className="accent">{accent}</span>
        </h1>
      </div>
    </div>
  );
}

// ── Subscription rows ───────────────────────────────────────────────
// Renders inside the Subscription card in the main Profile view. For free
// users: an upgrade CTA. For pro/lifetime: tier label, expiry date, and a
// Manage subscription deep link to iOS Settings.
function SubscriptionRows() {
  const ent = useEntitlements();
  const isFree = ent.tier === 'free';
  const isLifetime = ent.tier === 'lifetime';

  const tierLabel = isLifetime ? 'Lifetime'
    : ent.tier === 'pro' ? `Pro · ${ent.period === 'annual' ? 'Annual' : 'Monthly'}`
    : 'Free';

  const expiresLabel = (() => {
    if (isLifetime) return 'Yours forever';
    if (!ent.expiresAt) return null;
    try {
      const d = new Date(ent.expiresAt);
      const ds = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return ent.willRenew ? `Renews ${ds}` : `Cancels ${ds}`;
    } catch { return null; }
  })();

  const onUpgrade = () => {
    sfx.nav && sfx.nav();
    presentPaywall({ reason: 'profile' });
  };

  const onRestore = async () => {
    sfx.nav && sfx.nav();
    if (!isPurchasesAvailable()) return;
    await restorePurchases();
    refreshEntitlements();
  };

  const onManage = async () => {
    sfx.nav && sfx.nav();
    // Two paths based on which payment system owns this user's subscription.
    // On iOS we deep-link into Settings → Apple ID → Subscriptions (Apple's
    // own UI is the only way to cancel an IAP subscription). On desktop /
    // web the user paid through Stripe, so we open the Stripe Customer
    // Portal — a Stripe-hosted page that lets them change card, swap plans,
    // view invoices, and cancel.
    if (isIOS()) {
      try { openExternal('https://apps.apple.com/account/subscriptions'); } catch {}
      return;
    }
    try {
      const result = await openBillingPortal();
      if (!result.ok) {
        // No customer yet, or Stripe error. Fall back to the deployed app
        // root so the user at least lands somewhere working.
        console.warn('[profile] billing portal failed', result.error?.message);
      }
    } catch {}
  };

  if (isFree) {
    return (
      <>
        <div className="setting-row">
          <div className="ico"><Icon name="lock" size={15}/></div>
          <div className="lbl">Plan</div>
          <div className="det">Free</div>
        </div>
        <div
          className="setting-row"
          style={{ cursor: 'pointer', borderTop: '1px solid var(--stroke-1)' }}
          onClick={onUpgrade}
        >
          <div className="ico"><Icon name="sparkles" size={15}/></div>
          <div className="lbl">Upgrade to Pro</div>
          <Icon name="chevron-r" size={14} color="#6E7280"/>
        </div>
        <div
          className="setting-row"
          style={{ cursor: 'pointer', borderTop: '1px solid var(--stroke-1)' }}
          onClick={onRestore}
        >
          <div className="ico"><Icon name="refresh" size={15}/></div>
          <div className="lbl">Restore purchases</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="setting-row">
        <div className="ico"><Icon name="check" size={15}/></div>
        <div className="lbl">Plan</div>
        <div className="det">{tierLabel}</div>
      </div>
      {expiresLabel && (
        <div className="setting-row" style={{ borderTop: '1px solid var(--stroke-1)' }}>
          <div className="ico"><Icon name="calendar" size={15}/></div>
          <div className="lbl">{ent.willRenew && !isLifetime ? 'Renews' : isLifetime ? 'Tenure' : 'Cancels'}</div>
          <div className="det">{expiresLabel.replace(/^(Renews|Cancels)\s/, '')}</div>
        </div>
      )}
      {!isLifetime && (
        <div
          className="setting-row"
          style={{ cursor: 'pointer', borderTop: '1px solid var(--stroke-1)' }}
          onClick={onManage}
        >
          <div className="ico"><Icon name="external" size={15}/></div>
          <div className="lbl">Manage subscription</div>
          <Icon name="chevron-r" size={14} color="#6E7280"/>
        </div>
      )}
      <div
        className="setting-row"
        style={{ cursor: 'pointer', borderTop: '1px solid var(--stroke-1)' }}
        onClick={onRestore}
      >
        <div className="ico"><Icon name="refresh" size={15}/></div>
        <div className="lbl">Restore purchases</div>
      </div>
    </>
  );
}

// ── Privacy sub-page ────────────────────────────────────────────────
function PrivacyPage({ onBack }) {
  // Reactive consent state — re-renders when granted/revoked anywhere.
  const consent = useSyncExternalStore(
    subscribeConsent,
    () => getAIConsent(),
    () => null,
  );
  const granted = !!consent;

  const onToggle = () => {
    sfx.nav && sfx.nav();
    if (granted) revokeAIConsent();
    else grantAIConsent();
  };

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      <SubPageHeader eyebrow="Settings" title="Your" accent="Privacy" onBack={onBack}/>

      <div style={{ padding: '4px 20px 12px' }}>
        {/* AI features consent control — required by App Store guideline
            5.1.1(i) / 5.1.2(i). Lives at the top of the privacy page so it
            is one tap from the Privacy entry in Settings. */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 18,
          background: 'rgba(19,21,25,0.6)',
          backdropFilter: 'blur(30px)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            display: 'grid', placeItems: 'center',
            background: granted
              ? 'linear-gradient(180deg, rgba(71,166,255,0.25), rgba(45,125,224,0.18))'
              : 'rgba(255,255,255,0.06)',
            border: '1px solid var(--stroke-1)',
            color: granted ? 'var(--accent-hi)' : 'var(--fg-3)',
            flexShrink: 0,
          }}>
            <Icon name="brain" size={15}/>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
              color: 'var(--fg-1)',
            }}>
              AI features
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 11.5,
              color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.45,
            }}>
              {granted
                ? 'Allowed to send data to Anthropic, ElevenLabs, and OpenAI Whisper.'
                : 'Not allowed. The Agenda, Intel Brief, and narration will ask before sending data.'}
            </div>
          </div>
          <button
            onClick={onToggle}
            style={{
              padding: '8px 14px', borderRadius: 999,
              background: granted
                ? 'rgba(255,255,255,0.06)'
                : 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
              border: granted
                ? '1px solid var(--stroke-1)'
                : '1px solid rgba(71,166,255,0.55)',
              color: granted ? 'var(--fg-2)' : '#ffffff',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
              cursor: 'pointer', flexShrink: 0,
              boxShadow: granted ? 'none' : '0 6px 18px rgba(45,125,224,0.4)',
            }}
          >
            {granted ? 'Revoke' : 'Allow'}
          </button>
        </div>
      </div>

      <div style={{ padding: '4px 20px 20px' }}>
        <div style={{
          padding: 18,
          borderRadius: 18,
          background: 'rgba(19,21,25,0.6)',
          backdropFilter: 'blur(30px)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          fontFamily: 'var(--font-body)',
          fontSize: 12.5,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
        }}>
          <p style={{ margin: 0 }}>
            Consistency is built to keep as little data about you as
            possible, and to keep what it does store in your control.
          </p>

          <Section title="What we store">
            Your habits, daily check-ins, goals, display name, and agenda
            history. That is the whole picture. We do not collect analytics,
            device fingerprints, location, contacts, or advertising IDs.
          </Section>

          <Section title="Where it lives">
            If you sign in, your data is stored in Supabase (Postgres) under
            a row-level-security policy keyed to your user ID. Only you can
            read or write your rows. As a guest, everything stays on this
            device in local storage, and nothing is sent to our servers.
          </Section>

          <Section title="AI features">
            The first time you use the Agenda, Intel Brief, or narration,
            the app shows a one-time consent prompt that lists exactly
            what data will be sent and to which provider. Nothing is sent
            until you tap Continue. You can revoke that permission at any
            time using the toggle at the top of this page; once revoked,
            the prompt will appear again the next time an AI feature is
            used. Under our API terms, none of the providers below train
            their models on data sent through Consistency. AI requests
            are rate-limited to 20 per day per account.
          </Section>

          <Section title="Who we share with">
            We do not sell, rent, or share your data. The third-party
            processors involved are Supabase (database &amp; auth),
            Vercel (hosting for the API), Anthropic (Claude — receives
            the text you type when you build an agenda or brief),
            ElevenLabs (text-to-speech — receives the narration text of
            an Intel Brief when you tap Play), and OpenAI Whisper
            (speech-to-text — receives the audio you record when you use
            voice input on the Agenda).
          </Section>

          <Section title="Your controls">
            You can edit or delete any habit, check-in, goal, or agenda
            from the app. You can sign out at any time, which disconnects
            this device from the synced copy. To permanently delete your
            account and every row tied to it, use the Delete account
            button under your Profile. Deletion is immediate and cannot
            be undone.
          </Section>

          <Section title="Contact">
            Questions or general privacy inquiries: <span style={{ color: 'var(--accent-hi)' }}>privacy@proxera.io</span>
          </Section>

          <div style={{
            marginTop: 18, paddingTop: 14,
            borderTop: '1px solid var(--stroke-1)',
            fontSize: 10.5, color: 'var(--fg-4)',
          }}>
            Last updated April 2026
          </div>
        </div>
      </div>
    </div>
  );
}

// ── About sub-page ────────────────────────────────────────────────
function AboutPage({ onBack }) {
  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      <SubPageHeader eyebrow="About" title="Proxera" accent="Consistency" onBack={onBack}/>

      <div style={{ padding: '4px 20px 12px' }}>
        <div style={{
          padding: 20,
          borderRadius: 22,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.22), transparent 55%), rgba(19,21,25,0.72)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          textAlign: 'center',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px',
            background: 'var(--grad-cta)',
            display: 'grid', placeItems: 'center',
            boxShadow: '0 0 24px rgba(45,156,255,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
          }}>
            <Icon name="star" size={26} color="#fff" stroke={2}/>
          </div>
          <div className="grad-text" style={{
            fontFamily: 'var(--font-display)', fontWeight: 900,
            fontSize: 22, letterSpacing: '-0.02em',
          }}>
            Consistency
          </div>
          <div style={{
            fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)',
            marginTop: 4,
          }}>
            Version 1.0 · by Proxera
          </div>
        </div>
      </div>

      <div style={{ padding: '0 20px 20px' }}>
        <div style={{
          padding: 18,
          borderRadius: 18,
          background: 'rgba(19,21,25,0.6)',
          backdropFilter: 'blur(30px)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          fontFamily: 'var(--font-body)',
          fontSize: 12.5,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
        }}>
          <p style={{ margin: 0 }}>
            Consistency is a habit tracker for the kind of person who
            doesn't need another dashboard. They need a reason to show up
            today.
          </p>

          <Section title="The idea">
            Streaks don't build character; showing up does. Consistency
            rewards the small daily act: you open the app, you check in,
            you move on. Over a month, a year, a life, those checks add up
            to something the graph can't quite capture.
          </Section>

          <Section title="The agenda">
            Each morning, the app can turn a quick voice rant into a
            clean agenda: tasks, meetings, and a single focus for the
            day. It's optional. If you ignore it, it doesn't nag.
          </Section>

          <Section title="Built with">
            React, Vite, Supabase, Capacitor, and Anthropic's Claude. Made
            in small increments over many mornings, which felt right for
            the product.
          </Section>

          <Section title="Credits">
            Design and engineering by Noah Prose. Typeface: system display
            and body stacks. Icons: bespoke SVG set drawn for this app.
          </Section>

          <Section title="Support">
            Feature requests, bug reports, or just a hello:
            <span style={{ color: 'var(--accent-hi)' }}> hello@proxera.io</span>
          </Section>
        </div>
      </div>

      <div style={{ padding: '4px 20px 20px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
          © 2026 Proxera · All rights reserved
        </div>
      </div>
    </div>
  );
}

// Section heading used inside the Privacy and About cards. Keeps the
// type scale and spacing consistent across both sub-pages.
function Section({ title, children }) {
  return (
    <>
      <div className="eyebrow" style={{ fontSize: 9, marginTop: 16, marginBottom: 6 }}>
        {title}
      </div>
      <p style={{ margin: 0 }}>{children}</p>
    </>
  );
}

// ── Notifications settings row + page ─────────────────────────────
// One row that opens the full Notifications hub. The detail text
// summarizes how many push types are enabled across both modules
// (eveningRecap + notifications), which is what users typically
// scan for in iOS settings. Reactive to both stores so toggling
// from the hub updates the row immediately.
function RecapSettingRow({ onClick }) {
  const recap = useSyncExternalStore(subscribeRecapPrefs, getRecapPrefs, getRecapPrefs);
  const notif = useSyncExternalStore(subscribeNotifPrefs, getNotifPrefs, getNotifPrefs);
  const enabledCount =
    (recap.enabled ? 1 : 0)
    + (notif.streakProtection.enabled ? 1 : 0)
    + (notif.weeklyReview.enabled ? 1 : 0)
    + (notif.morningIntent.enabled ? 1 : 0);
  const detail = enabledCount === 0 ? 'All off' : `${enabledCount} on`;
  return (
    <div className="setting-row" style={{ cursor: 'pointer' }} onClick={onClick}>
      <div className="ico"><Icon name="bell" size={15}/></div>
      <div className="lbl">Notifications</div>
      <div className="det">{detail}</div>
      <Icon name="chevron-r" size={14} color="#6E7280"/>
    </div>
  );
}

// One row inside the notifications hub. Each notification type renders
// as a card with a left icon, a title + descriptor, a toggle pill, and
// (when enabled) an inline time picker.
function NotificationCard({
  iconName, title, descriptionOn, descriptionOff,
  enabled, onToggle, time, onTimeChange, weeklyDay,
}) {
  return (
    <div style={{
      padding: '14px 16px',
      borderRadius: 18,
      background: 'rgba(19,21,25,0.6)',
      backdropFilter: 'blur(30px)',
      border: '1px solid var(--stroke-1)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
      marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10,
          display: 'grid', placeItems: 'center',
          background: enabled
            ? 'linear-gradient(180deg, rgba(var(--accent-rgb),0.25), rgba(var(--accent-rgb),0.10))'
            : 'rgba(255,255,255,0.06)',
          border: '1px solid var(--stroke-1)',
          color: enabled ? 'var(--accent-hi)' : 'var(--fg-3)',
          flexShrink: 0,
        }}>
          <Icon name={iconName} size={15}/>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5, color: 'var(--fg-1)' }}>
            {title}
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.45 }}>
            {enabled ? descriptionOn : descriptionOff}
          </div>
        </div>
        <button
          onClick={onToggle}
          style={{
            padding: '8px 14px', borderRadius: 999,
            background: enabled
              ? 'rgba(255,255,255,0.06)'
              : 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
            border: enabled ? '1px solid var(--stroke-1)' : '1px solid rgba(71,166,255,0.55)',
            color: enabled ? 'var(--fg-2)' : '#ffffff',
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
            cursor: 'pointer', flexShrink: 0,
            boxShadow: enabled ? 'none' : '0 6px 18px rgba(45,125,224,0.4)',
          }}
        >{enabled ? 'Turn off' : 'Turn on'}</button>
      </div>

      {enabled && time != null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12, paddingTop: 12, borderTop: '1px solid var(--stroke-1)' }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)' }}>
            {weeklyDay ? `Every ${weeklyDay} at` : 'Daily at'}
          </div>
          <input
            type="time"
            value={time}
            onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && onTimeChange(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 10,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--stroke-2)',
              color: 'var(--fg-1)',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13,
              cursor: 'pointer',
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        </div>
      )}
    </div>
  );
}

function RecapSettingsPage({ onBack }) {
  const recap = useSyncExternalStore(subscribeRecapPrefs, getRecapPrefs, getRecapPrefs);
  const notif = useSyncExternalStore(subscribeNotifPrefs, getNotifPrefs, getNotifPrefs);

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      <SubPageHeader eyebrow="Settings" title="Your" accent="Notifications" onBack={onBack}/>

      <div style={{ padding: '4px 20px 20px' }}>
        {/* 1. Evening Recap (lives in eveningRecap.js — fires at the
            user's chosen evening time and auto-opens the recap modal
            on the dashboard). */}
        <NotificationCard
          iconName="bell"
          title="Evening recap"
          descriptionOn={`Tonight, at ${formatRecapTime(recap.time)}, we will ask you to speak or tap your day in.`}
          descriptionOff="No daily reminder. You can still recap manually any time."
          enabled={recap.enabled}
          onToggle={() => { sfx.nav && sfx.nav(); setRecapEnabled(!recap.enabled); }}
          time={recap.time}
          onTimeChange={(v) => { setRecapTime(v); sfx.tick && sfx.tick(); }}
        />

        {/* 2. Streak Protection — fires earlier in the evening so a
            user with a streak in jeopardy gets a chance to act before
            the recap modal opens. */}
        <NotificationCard
          iconName="flame"
          title="Streak protection"
          descriptionOn={`Tap reminder if a streak is at risk that night, at ${formatNotifTime(notif.streakProtection.time)}.`}
          descriptionOff="Streaks can quietly end at midnight without you noticing."
          enabled={notif.streakProtection.enabled}
          onToggle={() => { sfx.nav && sfx.nav(); setNotifTypeEnabled('streakProtection', !notif.streakProtection.enabled); }}
          time={notif.streakProtection.time}
          onTimeChange={(v) => { setNotifTypeTime('streakProtection', v); sfx.tick && sfx.tick(); }}
        />

        {/* 3. Weekly Review — Sundays. */}
        <NotificationCard
          iconName="calendar"
          title="Weekly review"
          descriptionOn={`A Sunday recap of how your week landed, at ${formatNotifTime(notif.weeklyReview.time)}.`}
          descriptionOff="Sundays without a check in. Set goals for next week, fast."
          enabled={notif.weeklyReview.enabled}
          onToggle={() => { sfx.nav && sfx.nav(); setNotifTypeEnabled('weeklyReview', !notif.weeklyReview.enabled); }}
          time={notif.weeklyReview.time}
          onTimeChange={(v) => { setNotifTypeTime('weeklyReview', v); sfx.tick && sfx.tick(); }}
          weeklyDay="Sunday"
        />

        {/* 4. Morning Intent — opt-in. */}
        <NotificationCard
          iconName="sun"
          title="Morning intent"
          descriptionOn={`A nudge at ${formatNotifTime(notif.morningIntent.time)} to pick a habit to start the day with.`}
          descriptionOff="Quiet mornings. We will not push first thing."
          enabled={notif.morningIntent.enabled}
          onToggle={() => { sfx.nav && sfx.nav(); setNotifTypeEnabled('morningIntent', !notif.morningIntent.enabled); }}
          time={notif.morningIntent.time}
          onTimeChange={(v) => { setNotifTypeTime('morningIntent', v); sfx.tick && sfx.tick(); }}
        />

        {/* 5. Quiet Hours — master setting that suppresses any push
            whose scheduled time falls inside the window. Also gates
            the event-fired Brief Ready when a brief completes during
            the quiet window. */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 18,
          background: 'rgba(19,21,25,0.6)',
          backdropFilter: 'blur(30px)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              display: 'grid', placeItems: 'center',
              background: notif.quietHours.enabled
                ? 'linear-gradient(180deg, rgba(var(--accent-rgb),0.25), rgba(var(--accent-rgb),0.10))'
                : 'rgba(255,255,255,0.06)',
              border: '1px solid var(--stroke-1)',
              color: notif.quietHours.enabled ? 'var(--accent-hi)' : 'var(--fg-3)',
              flexShrink: 0,
            }}>
              <Icon name="moon" size={15}/>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5, color: 'var(--fg-1)' }}>
                Quiet hours
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.45 }}>
                {notif.quietHours.enabled
                  ? `Nothing will ping you between ${formatNotifTime(notif.quietHours.start)} and ${formatNotifTime(notif.quietHours.end)}.`
                  : 'When on, no push fires inside the window you set.'}
              </div>
            </div>
            <button
              onClick={() => { sfx.nav && sfx.nav(); setNotifQuietHours({ enabled: !notif.quietHours.enabled }); }}
              style={{
                padding: '8px 14px', borderRadius: 999,
                background: notif.quietHours.enabled ? 'rgba(255,255,255,0.06)' : 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
                border: notif.quietHours.enabled ? '1px solid var(--stroke-1)' : '1px solid rgba(71,166,255,0.55)',
                color: notif.quietHours.enabled ? 'var(--fg-2)' : '#ffffff',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 12,
                cursor: 'pointer', flexShrink: 0,
                boxShadow: notif.quietHours.enabled ? 'none' : '0 6px 18px rgba(45,125,224,0.4)',
              }}
            >{notif.quietHours.enabled ? 'Turn off' : 'Turn on'}</button>
          </div>

          {notif.quietHours.enabled && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 12, paddingTop: 12, borderTop: '1px solid var(--stroke-1)' }}>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)' }}>From / to</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="time"
                  value={notif.quietHours.start}
                  onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && setNotifQuietHours({ start: e.target.value })}
                  style={timeInputStyle}
                />
                <input
                  type="time"
                  value={notif.quietHours.end}
                  onChange={(e) => /^\d{2}:\d{2}$/.test(e.target.value) && setNotifQuietHours({ end: e.target.value })}
                  style={timeInputStyle}
                />
              </div>
            </div>
          )}
        </div>

        {/* Help text */}
        <div style={{
          marginTop: 4, padding: '10px 14px', borderRadius: 12,
          background: 'rgba(var(--accent-rgb), 0.06)',
          border: '1px solid rgba(var(--accent-rgb), 0.18)',
          fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-2)', lineHeight: 1.5,
        }}>
          The Brief Ready notification fires whenever a daily Intel Brief finishes generating in the background. It does not have a schedule and respects your quiet hours.
        </div>
      </div>
    </div>
  );
}

const timeInputStyle = {
  padding: '8px 12px', borderRadius: 10,
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--stroke-2)',
  color: 'var(--fg-1)',
  fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13,
  cursor: 'pointer',
  fontVariantNumeric: 'tabular-nums',
};

// ── Appearance sub-page ─────────────────────────────────────────────
// Lets the user pick between two themes. Selection writes through
// themeStore, which immediately applies `data-theme` to <html> so the
// whole app re-skins live — no reload needed.
//
// Each option is a PickerRow with a short description of the aesthetic
// so the names 'Cosmic' and 'Professional' don't have to carry the
// whole idea on their own.
function AppearancePage({ onBack }) {
  const theme = useSyncExternalStore(themeStore.subscribe, themeStore.getSnapshot, themeStore.getSnapshot);

  const set = (next) => {
    themeStore.setTheme(next);
    sfx.toast && sfx.toast();
  };

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      <SubPageHeader eyebrow="Settings" title="Your" accent="Appearance" onBack={onBack}/>

      {/* Intro card */}
      <div style={{ padding: '4px 20px 14px' }}>
        <div style={{
          padding: 16,
          borderRadius: 18,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.12), transparent 55%), rgba(19,21,25,0.72)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          fontFamily: 'var(--font-body)',
          fontSize: 12.5,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
        }}>
          Pick the mood Consistency wears. Four themes ship today.
          Cosmic for the default electric vibe, Executive for a calm
          dark office aesthetic, Relaxed for a warm paper-white canvas,
          and Girlboss for a pink celebration with hearts where the
          checks used to be. Changes apply instantly.
        </div>
      </div>

      {/* Theme picker */}
      <div style={{ padding: '0 20px 24px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Theme</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {/* Dot colors here mirror each theme's actual accent so the
              picker doubles as a palette preview. Pro Dark and Pro Light
              used to share the same #1E7FFF dot, which made them look
              like copies — now Pro Dark uses a deeper editorial blue and
              Pro Light uses a brighter one, matching what the theme
              actually renders in the rest of the app. */}
          <PickerRow
            accent="#2D9CFF"
            title="Cosmic"
            subtitle="Electric blue, gradient accents, drifting ambient light"
            selected={theme === 'cosmic'}
            onClick={() => set('cosmic')}
          />
          <PickerRow
            accent="#0A47B8"
            title="Executive"
            subtitle="Deep editorial blue, flat surfaces, calm office aesthetic"
            selected={theme === 'professional-dark'}
            onClick={() => set('professional-dark')}
          />
          <PickerRow
            accent="#6E7E5F"
            title="Relaxed"
            subtitle="Warm sand canvas, moss-khaki accents, soft cream cards"
            selected={theme === 'professional-light'}
            onClick={() => set('professional-light')}
          />
          <PickerRow
            accent="#FF3E6C"
            title="Girlboss"
            subtitle="Hot pink accents, hearts instead of checks"
            selected={theme === 'girlboss'}
            onClick={() => set('girlboss')}
            isLast
          />
        </div>
      </div>
    </div>
  );
}

// ── Intelligence Brief settings sub-page ────────────────────────────
// Three pickers, all persisted via briefStore prefs:
//   1. Default bundle — the lens that preselects on the Intel tab
//   2. Narrator voice — ElevenLabs voice ID used for TTS playback
//   3. Retention window — auto-delete old briefs after N days
// Each picker is a radio-style row group. We keep state hyperlocal and
// write through on every change so the user never needs a Save button.
function IntelligenceSettingsPage({ onBack }) {
  const [prefs, setPrefs] = useState(() => briefStore.getPrefs());

  const set = (patch) => {
    const next = briefStore.updatePrefs(patch);
    setPrefs(next);
    sfx.toast && sfx.toast();
  };

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      <SubPageHeader eyebrow="Settings" title="Intelligence" accent="Brief" onBack={onBack}/>

      {/* Intro card */}
      <div style={{ padding: '4px 20px 14px' }}>
        <div style={{
          padding: 16,
          borderRadius: 18,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.12), transparent 55%), rgba(19,21,25,0.72)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--gold-ring)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
          fontFamily: 'var(--font-body)',
          fontSize: 12.5,
          color: 'var(--fg-2)',
          lineHeight: 1.55,
        }}>
          Control how your daily Intel Briefs look, sound, and
          accumulate on this device. Every change saves the moment you tap it.
        </div>
      </div>

      {/* Default bundle */}
      <div style={{ padding: '0 20px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Default Bundle</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {BUNDLES.map((b, i) => (
            <PickerRow
              key={b.id}
              accent={b.accent}
              title={b.name}
              subtitle={b.description}
              selected={prefs.defaultBundle === b.id}
              onClick={() => set({ defaultBundle: b.id })}
              isLast={i === BUNDLES.length - 1 && prefs.defaultBundle !== 'custom'}
            />
          ))}
          <PickerRow
            accent="#41B3FF"
            title="Custom"
            subtitle="Write your own focus prompt each day"
            selected={prefs.defaultBundle === 'custom'}
            onClick={() => set({ defaultBundle: 'custom' })}
            isLast
          />
        </div>
      </div>

      {/* Voice */}
      <div style={{ padding: '0 20px 14px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Narrator Voice</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {VOICE_OPTIONS.map((v, i) => (
            <PickerRow
              key={v.id}
              accent="#2D9CFF"
              title={v.name}
              subtitle={v.tag}
              selected={prefs.voicePreference === v.id}
              onClick={() => set({ voicePreference: v.id })}
              isLast={i === VOICE_OPTIONS.length - 1}
            />
          ))}
        </div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-4)', padding: '8px 4px 0', lineHeight: 1.5 }}>
          Voice changes apply to the next brief you play. Briefs already cached
          in this session continue to use the voice they were generated with.
        </div>
      </div>

      {/* Retention */}
      <div style={{ padding: '0 20px 24px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Auto-delete Old Briefs</div>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'rgba(19,21,25,0.6)', backdropFilter: 'blur(30px)', border: '1px solid var(--stroke-1)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
          {RETENTION_OPTIONS.map((r, i) => (
            <PickerRow
              key={String(r.value)}
              accent="#2D9CFF"
              title={r.label}
              subtitle={r.sub}
              selected={prefs.autoDeleteAfterDays === r.value}
              onClick={() => set({ autoDeleteAfterDays: r.value })}
              isLast={i === RETENTION_OPTIONS.length - 1}
            />
          ))}
        </div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-4)', padding: '8px 4px 0', lineHeight: 1.5 }}>
          Briefs live in local storage on this device only. Retention trims
          the list lazily the next time you open the Intelligence tab.
        </div>
      </div>
    </div>
  );
}

// Tap-to-select row used by every group in IntelligenceSettingsPage. The
// left dot uses the group's accent color so the three sections stay
// visually distinct without adding more chrome.
function PickerRow({ accent, title, subtitle, selected, onClick, isLast }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px',
        background: selected ? 'rgba(45,156,255,0.08)' : 'transparent',
        border: 'none',
        borderBottom: isLast ? 'none' : '1px solid var(--stroke-1)',
        color: 'var(--fg-2)',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{
        width: 10, height: 10, borderRadius: 999,
        background: accent,
        boxShadow: `0 0 10px ${accent}`,
        flexShrink: 0,
      }}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--fg-1)', letterSpacing: '-0.01em' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2, lineHeight: 1.35 }}>
            {subtitle}
          </div>
        )}
      </div>
      <div style={{
        width: 18, height: 18, borderRadius: 999,
        border: selected ? '2px solid var(--accent-hi, #47A6FF)' : '2px solid var(--stroke-2)',
        display: 'grid', placeItems: 'center',
        flexShrink: 0,
      }}>
        {selected && (
          <div style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--accent-hi, #47A6FF)' }}/>
        )}
      </div>
    </button>
  );
}
